import { Button, Checkbox, Col, Form, Input, Row, Select, Tooltip } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Panel from '../../../../shared/components/card/Panel';
import PageHeader from '../../../../shared/components/pageHeader/PageHeader';
import {
  APPLICATION_ID,
  GUTTOR_SPACE,
  GUTTOR_SPACE_SMALL,
  INVENTORY_MESSAGE_CONSTANTS,
  INVENTROY_CONSTANTS,
  TOOLTIP_TEXT,
} from '../../../../shared/constants/commonConstants';
import { FETCH_DAYCARE_PROCEDURE_LIST, INVENTORY_SAVE_IP_PACKAGE } from '../../../../shared/constants/urlEndPoints';
import {
  FetchInPatientProcedureResponse,
  SaveInclusionListDTO,
  SaveInPatientPackageDTO,
  SaveInPatientPackageRequest,
  SaveInPatientPackageResponse,
  SavePricingListDTO,
} from '../../../../shared/models/inPatient.model';
import { LookUpDetail } from '../../../../shared/models/lookUp.model';
import ApplicationDetail from '../../../../shared/services/applicationDetail.service';
import SendRequest from '../../../../shared/services/sendRequest.service';
import {
  fetchDayCareDepartmentMaster,
  fetchDayCareInclusionMaster,
} from '../../../../store/actions/hospitalAction';
import {
  base64UrlEncode, CalculateDiscountPercentage, CalculateFinalPrice, centeredNotification, error,
  extractSecondLevelBaseUrl, GetFirstElement, success,
} from '../../../../utils/utility';
import { useHistory } from 'react-router-dom';
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, convertFromHTML, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import '../../../../shared/components/editor/HtmlEditor.less';
import { ReactComponent as AddIcon } from '../../../../assets/images/svg/i_add.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/images/svg/i_close.svg';

const DaycarePackageForm = ({
  backUrl,
  pageTitle,
  data,
  path,
}: any): ReactElement => {
  const [packageForm] = Form.useForm();
  const dispatch = useDispatch();
  const history = useHistory();
  const [procedureList, setProcedureList] = useState(null as any);
  const [pricingLists, setPricingList] = useState(null as any);
  const [discount, setDiscount] = useState(0);
  const [editorHtmlText, setEditorHtmlText] = useState('<p></p>');
  const [contentState, setContentState] = useState(null as any);
  const inclusionMasterList = useSelector(
    ({ hospital: { dayCareinclusionMaster } }: any): any =>
      dayCareinclusionMaster,
  );
  const departmentMasterList = useSelector(
    ({ hospital: { dayCaredepartmentMaster } }: any): any =>
      dayCaredepartmentMaster,
  );
  useEffect((): void => {
    if (!inclusionMasterList) {
      dispatch(fetchDayCareInclusionMaster());
    }
    if (!departmentMasterList) {
      dispatch(fetchDayCareDepartmentMaster());
    }
  }, []);
  useEffect((): void => {
    setInitialFields();
  }, [data]);

  const setInitialFields = (): void => {
    if (data?.description) {
      setEditorHtmlText(data.description);
      const contentBlocks = convertFromHTML(data.description);
      // tslint:disable-next-line: no-shadowed-variable
      const contentState = ContentState.createFromBlockArray(contentBlocks?.contentBlocks);
      const raw = convertToRaw(contentState);
      setContentState(raw);
    }
    packageForm.setFieldsValue({
      discountAll: '',
      packageDescription: editorHtmlText,
      department: data?.departmentName,
      procedure: data?.procedureName,
      lengthOfStay: data?.lengthOfStay,
      pricingList: data?.pricingDetails ? data?.pricingDetails.map((v: any): any => {
        return {
          id: v.id,
          price: v.price,
          discountPercent: CalculateDiscountPercentage(
            v.price,
            v.discountedPrice,
          ),
          finalPrice: v.discountedPrice,
          typeId: v.typeId,
          isActive: v.isActive,
        };
      }) : [{
        id: 0,
        price: 0,
        discountPercent: discount,
        finalPrice: 0,
        typeId: 1,
        isActive: true,
      }],
      inclusions: data?.inclusions
        ?.filter((x: any): boolean => x.isActive)
        .map((x: any): string => {
          return x.inclusionName;
        }),
    });
  };
  const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };

  const onDepartmentChange = async (department: string): Promise<void> => {
    packageForm.setFieldsValue({
      procedure: '',
    });
    const depId = departmentMasterList.find((x: LookUpDetail): boolean => {
      return x.value === department;
    })?.id;
    if (depId > 0) {
      fetchProcedureList(depId);
    }
  };

  const fetchProcedureList = async (depId: number): Promise<void> => {
    if (depId <= 0) {
      return;
    }
    const url = `${process.env.REACT_APP_INVENTORY_API_BASE_URL}${FETCH_DAYCARE_PROCEDURE_LIST}${depId}`;
    const result: any = await SendRequest.instance.get<
      FetchInPatientProcedureResponse
    >(url);
    if (result && result.isSuccess) {
      setProcedureList(
        result.procedureList.map((x: any): any => {
          return {
            id: x.id,
            name: x.name,
            value: x.name,
          };
        }),
      );
    }
  };

  const onDiscountPercentChange = async (discountPercent: any, index: number): Promise<void> => {
    discountPercent = discountPercent === undefined ? 0 : discountPercent;
    const pricing = packageForm.getFieldValue('pricingList');
    if (pricing && pricing[index]) {
      pricing[index].discountPercent = discountPercent;
      pricing[index].finalPrice = CalculateFinalPrice(Number(pricing[index].price || 0), Number(discountPercent || 0));
      packageForm.setFieldsValue({ pricingList: pricing });
    }
  };

  const onPriceChange = async (price: any, index: number): Promise<void> => {
    price = price === undefined ? 0 : price;
    const pricing = packageForm.getFieldValue('pricingList');
    if (pricing && pricing[index]) {
      pricing[index].price = price;
      pricing[index].finalPrice = CalculateFinalPrice(
        Number(price || 0),
        Number(pricing[index].discountPercent || 0),
      );
      packageForm.setFieldsValue({ pricingList: pricing });
    }
  };
  const onFormSubmit = async (): Promise<void> => {
    const validationError = await packageForm.validateFields();
    if (validationError.errorFields && validationError.errorFields.length > 0) {
      return;
    }
    savePackage();
  };

  const savePackage = async (
    isRemovePackage: boolean = false,
  ): Promise<void> => {
    const contentBlocks = convertFromHTML(editorHtmlText);
    const contentText = ContentState.createFromBlockArray(contentBlocks?.contentBlocks);
    const raw = convertToRaw(contentText);
    if (editorHtmlText === null || editorHtmlText === '' || editorHtmlText === undefined
      || raw.blocks.length === 0 || raw.blocks[0].text?.trim().length === 0) {
      error(INVENTORY_MESSAGE_CONSTANTS.enterDescription);
      return;
    }
    const packageDetails = packageForm.getFieldsValue();
    const ipPackage: SaveInPatientPackageDTO = {
      id: data?.id,
      profileId: data?.profileId || ApplicationDetail.instance.getProfileId(),
      statusName: data?.statusName || 'SUBMIT',
      packageType: 'DayCare',
      description: editorHtmlText,
      lengthOfStay: packageDetails.lengthOfStay,
      isActive: !isRemovePackage,
      procedureName: packageDetails.procedure,
      procedureId: procedureList
        ? procedureList.find(
          (x: any): any => x.name === packageDetails.procedure,
        )?.id
        : data?.procedureId,
      departmentId: departmentMasterList?.find(
        (x: any): any => x.value === packageDetails.department,
      )?.id,
      pricingList: pricingLists ? pricingLists?.map(
        (pricing: any): SavePricingListDTO => {
          return {
            id: pricing.id,
            isActive: pricing?.isActive === false ? false : true,
            typeId: pricing.typeId,
            price: pricing.price,
            discountedPrice: pricing.finalPrice,
            packageId: data?.id,
            statusName: data?.statusName || 'SUBMIT',
          };
        },
      ) : packageDetails?.pricingList?.map(
        (pricing: any): SavePricingListDTO => {
          return {
            id: pricing.id,
            isActive: pricing?.isActive === false ? false : true,
            typeId: pricing.typeId,
            price: pricing.price,
            discountedPrice: pricing.finalPrice,
            packageId: data?.id,
            statusName: data?.statusName || 'SUBMIT',
          };
        },
      ),
    };
    const inclusionList: SaveInclusionListDTO[] =
      data?.inclusions?.map(
        (inclusion: any): SaveInclusionListDTO => {
          let inc: SaveInclusionListDTO = {
            id: inclusion.id,
            packageId: inclusion.packageId,
            statusName: inclusion.statusName,
            inclusionId: inclusion.inclusionId,
            isActive: inclusion.isActive,
          };
          if (!packageDetails.inclusions.includes(inclusion.inclusionName)) {
            inc = { ...inc, isActive: false };
          }
          return inc;
        },
      ) || [];
    packageDetails.inclusions?.forEach((inclusion: string): void => {
      if (
        !data ||
        !data.inclusions?.some((x: any): any => x.inclusionName === inclusion)
      ) {
        inclusionList.push({
          id: 0,
          packageId: data?.id,
          statusName: data?.statusName || 'SUBMIT',
          isActive: true,
          inclusionId: inclusionMasterList?.find(
            (x: any): any => x.value === inclusion,
          )?.id,
        });
      }
    });
    ipPackage.inclusionList = inclusionList;
    const payload: SaveInPatientPackageRequest = {
      package: ipPackage,
      applicationId: APPLICATION_ID,
    };
    const url =
      process.env.REACT_APP_INVENTORY_API_BASE_URL + INVENTORY_SAVE_IP_PACKAGE;
    const result: SaveInPatientPackageResponse | any = await SendRequest.instance.post<
      SaveInPatientPackageRequest,
      SaveInPatientPackageResponse
    >(url, payload);
    if (result && result.isSuccess) {
      success('Package saved successfully');
      history.push(extractSecondLevelBaseUrl(path) + INVENTROY_CONSTANTS.daycarePackageDetailRoute +
        base64UrlEncode(GetFirstElement(result.ids)));
    } else if (!result.isSuccess) {
      centeredNotification(result.errorMessage);
    } else {
      error('Error Occurred');
    }
  };
  const addPrice = (): void => {
    const pricingListLength = packageForm.getFieldValue('pricingList')?.length;
    const pricingList = [{
      id: '',
      price: '',
      discountPercent: discount,
      finalPrice: '',
      isActive: true,
      typeId: pricingListLength + 1,
    }];
    if (pricingListLength <= 4) {
      packageForm.setFieldsValue({ 'pricingList': [...packageForm.getFieldValue('pricingList'), ...pricingList] });
    }
  };

  const removePrice = (typeId: number): void => {
    const temp = packageForm.getFieldValue('pricingList');
    const filteredPrice = temp.filter((vv: any, index: number): any =>
      typeId !== index + 1,
    );
    setPricingList(temp.map((v: any, index: any): any => {
      if (typeId !== index + 1) {
        return {
          ...v,
        };
      } else {
        return {
          ...v,
          isActive: false,
        };
      }
    }));
    packageForm.setFieldsValue({ 'pricingList': [...filteredPrice] });
  };

  const onDiscountPercentChangeToAll = (discountPercent: any): void => {
    discountPercent = discountPercent === undefined ? 0 : discountPercent;
    const pricing = packageForm.getFieldValue('pricingList');
    if (pricing && pricing.length > 0) {
      pricing.map((val: any, i: number): void => {
        pricing[i].discountPercent = discountPercent;
        pricing[i].finalPrice = CalculateFinalPrice(Number(pricing[i].price || 0), Number(discountPercent || 0));
        packageForm.setFieldsValue({ pricingList: pricing });
      });
      setDiscount(discountPercent);
    }
  };
  const onEditorContentChange = (editorText: any): any => {
    setEditorHtmlText(draftToHtml(editorText));
  };
  return (
    <>
      <PageHeader
        backUrl={backUrl}
        pageTitle={pageTitle}
        rightComponent={
          (): ReactElement =>
            <Row gutter={GUTTOR_SPACE}>
              <Col md={18} />
              <Col md={6}>
                <div className="text-right">
                  <Button className="btn-form-submit" size={'middle'}
                    onClick={(): any => onFormSubmit()}> Submit
                  </Button>
                </div>
              </Col>
            </Row>}
      />
      <div className="ant-tabs-content-holder tab-custom-container">
        <Form {...layout} form={packageForm}>
          <Row gutter={GUTTOR_SPACE}>
            <Col md={16}>
              <Panel title={'Package Details'}>
                <Row gutter={GUTTOR_SPACE}>
                  <Col md={12}>
                    <Form.Item
                      label="Department"
                      name="department"
                      rules={[
                        {
                          required: true,
                          message: 'Please select department!',
                        },
                      ]}
                    >
                      <Select
                        placeholder="Department name"
                        optionLabelProp="label"
                        allowClear={true}
                        options={departmentMasterList?.map((x: any): any => {
                          return {
                            name: x.value,
                            value: x.value,
                            key: x.id,
                          };
                        })}
                        onChange={onDepartmentChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col md={12}>
                    <Form.Item
                      label="Procedure"
                      name="procedure"
                      rules={[
                        {
                          required: true,
                          message: 'Please select procedure!',
                        },
                      ]}
                    >
                      <Select
                        placeholder="Procedure name"
                        optionLabelProp="label"
                        allowClear={true}
                        options={procedureList}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={GUTTOR_SPACE}>
                  <Col md={24}>
                    <Form.Item
                      label="Package description"
                      name="packageDescription"
                      className="mb-0"
                    >
                      <Editor
                        editorClassName="scroll-bar editor-style"
                        contentState={contentState}
                        onContentStateChange={onEditorContentChange}
                        toolbar={{
                          options: ['inline', 'blockType', 'fontSize',
                            'list', 'textAlign', 'colorPicker', 'link', 'history'],
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>
              <Panel title="Pricing Details"
                extra={<Row gutter={GUTTOR_SPACE_SMALL}>
                  <Col md={15} className="pr-0"><p className="label-title text-right" style={{ marginTop: 5 }}>
                    Discount for all sessions</p></Col>
                  <Col md={9}><Form.Item
                    name="discountAll"
                    className="mb-0"
                  >
                    <Input type="number"
                      min="0"
                      max="100"
                      suffix="%" onChange={(e: any): any => onDiscountPercentChangeToAll(e.target.value)} />
                  </Form.Item></Col>
                </Row>}>
                <Form.List name="pricingList">
                  {(fields?: any): any => {
                    return (<div>
                      <Row gutter={GUTTOR_SPACE}>
                        {fields?.map((field: any, index: number): any => (
                          <>
                            {/* {field.isActive && */}
                            <Col md={21} key={field.id}>
                              <Row gutter={GUTTOR_SPACE}>
                                <Col md={2}>
                                  <Form.Item label="&nbsp;"
                                    initialValue={index + 1}
                                    className="m-b-10"
                                    name={[field.name, 'typeId']}
                                    fieldKey={[field.fieldKey, 'typeId']}>
                                    <p className="session-count">{index + 1}</p>
                                  </Form.Item>
                                </Col>
                                <Col md={8}>
                                  <Form.Item
                                    className="m-b-10"
                                    label="Price"
                                    name={[field.name, 'price']}
                                    fieldKey={[field.fieldKey, 'price']}
                                    rules={
                                      [
                                        {
                                          required: true,
                                          message: 'Please enter price',
                                        },
                                      ]}
                                  >
                                    <Input type="number" prefix="₹"
                                      onChange={(e: any): any => onPriceChange(e.target.value, index)} />
                                  </Form.Item>
                                </Col>
                                <Col md={5}>
                                  <Form.Item
                                    label="Discount"
                                    name={[field.name, 'discountPercent']}
                                    fieldKey={[field.fieldKey, 'discountPercent']}
                                    className="m-b-10"
                                  >
                                    <Input type="number"
                                      min="0"
                                      max="100"
                                      suffix="%"
                                      onChange={(e: any): any => onDiscountPercentChange(e.target.value, index)} />
                                  </Form.Item>
                                </Col>
                                <Col md={7}>
                                  <Form.Item
                                    label="Discounted price"
                                    name={[field.name, 'finalPrice']}
                                    fieldKey={[field.fieldKey, 'finalPrice']}
                                    className="m-b-10"
                                  >
                                    <Input type="number"
                                      prefix="₹" disabled={true} />
                                  </Form.Item>
                                </Col>
                                <Col md={2}>
                                  {index === packageForm.getFieldValue('pricingList')?.length - 1 &&
                                    packageForm.getFieldValue('pricingList')?.length !== 1 &&
                                    <Form.Item label="&nbsp;" className="mb-0" name={[field.name, 'typeId']}
                                      fieldKey={[field.fieldKey, 'typeId']}>
                                      <div className="removeprice-btn" style={{ cursor: 'pointer' }}
                                        onClick={(): void => removePrice(index + 1)}>
                                        <Tooltip title={TOOLTIP_TEXT.remove}><CloseIcon className="fill-close" /></Tooltip>
                                      </div>
                                    </Form.Item>
                                  }
                                </Col>
                              </Row>
                            </Col>
                            {/* } */}
                          </>
                        ))}
                        <Col md={3}>
                          {packageForm.getFieldValue('pricingList')?.length < 5 &&
                            <>
                              <Form.Item label="&nbsp;">
                                <div className="addprice-btn" style={{ cursor: 'pointer' }} onClick={addPrice}>
                                <Tooltip title={TOOLTIP_TEXT.add}><AddIcon className="fill-primary" /></Tooltip>
                                </div>
                              </Form.Item>
                            </>}
                        </Col>
                      </Row>
                    </div>);
                  }}
                </Form.List>
              </Panel>
            </Col>
            <Col md={8}>
              <Panel title="Inclusions">
                {inclusionMasterList &&
                  <Form.Item
                    name="inclusions"
                  >
                    <Checkbox.Group
                      className="inclusion-checkbox"
                      options={inclusionMasterList
                        .filter((x: any): boolean => x.isActive)
                        .map((x: any): any => {
                          return x.value;
                        })}
                    />
                  </Form.Item>
                }
              </Panel>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
};

export default DaycarePackageForm;
